import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { fetchProfile } from '../../store/authSlice';

export default function CitizenProfile() {
  const { user } = useSelector(s => s.auth);
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    name: '', email: '', address: '', city: '', state: '', pincode: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    api.get('/auth/profile').then(r => {
      if (r.user) {
        setForm({
          name:    r.user.name    || '',
          email:   r.user.email   || '',
          address: r.user.address || '',
          city:    r.user.city    || '',
          state:   r.user.state   || '',
          pincode: r.user.pincode || '',
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Name is required');
    setSaving(true);
    try {
      const res = await api.put('/auth/profile', form);
      if (res.success) {
        toast.success('Profile updated successfully!');
        dispatch(fetchProfile());
        setEditMode(false);
      }
    } catch (err) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">View and edit your personal information</p>
        </div>
        {!editMode && (
          <button onClick={() => setEditMode(true)} className="btn-primary text-sm">
            ✏️ Edit Profile
          </button>
        )}
      </div>

      {/* Mobile — non-editable */}
      <div className="card mb-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl">
            👤
          </div>
          <div>
            <p className="font-bold text-lg text-gray-800">{form.name || 'Citizen'}</p>
            <p className="text-sm text-gray-500">+91 {user?.mobile}</p>
            <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
              ✓ Verified
            </span>
          </div>
        </div>
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-600">
          📱 Mobile number cannot be changed. It is your login identity.
        </div>
      </div>

      {/* Profile Form */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-4">
          {editMode ? '✏️ Edit Details' : 'Personal Details'}
        </h3>

        {!editMode ? (
          /* View Mode */
          <div className="space-y-3">
            {[
              { label: 'Full Name',    value: form.name },
              { label: 'Email',        value: form.email       || '—' },
              { label: 'Address',      value: form.address     || '—' },
              { label: 'City',         value: form.city        || '—' },
              { label: 'State',        value: form.state       || '—' },
              { label: 'Pincode',      value: form.pincode     || '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex gap-4 py-2 border-b border-gray-50 last:border-0">
                <span className="text-xs text-gray-400 w-28 flex-shrink-0 mt-0.5">{label}</span>
                <span className="text-sm text-gray-800 font-medium">{value}</span>
              </div>
            ))}
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="form-label">Full Name <span className="text-red-500">*</span></label>
              <input className="form-input" value={form.name}
                onChange={e => f('name', e.target.value)} required
                placeholder="Enter your full name" />
            </div>

            <div>
              <label className="form-label">Email Address</label>
              <input type="email" className="form-input" value={form.email}
                onChange={e => f('email', e.target.value)}
                placeholder="email@example.com" />
            </div>

            <div>
              <label className="form-label">Address</label>
              <textarea className="form-input" rows={3} value={form.address}
                onChange={e => f('address', e.target.value)}
                placeholder="House no., Street, Area" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="form-label">City</label>
                <input className="form-input" value={form.city}
                  onChange={e => f('city', e.target.value)} placeholder="City" />
              </div>
              <div>
                <label className="form-label">State</label>
                <input className="form-input" value={form.state}
                  onChange={e => f('state', e.target.value)} placeholder="State" />
              </div>
              <div>
                <label className="form-label">Pincode</label>
                <input className="form-input" value={form.pincode}
                  onChange={e => f('pincode', e.target.value.replace(/\D/,'').slice(0,6))}
                  placeholder="000000" maxLength={6} />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-primary flex-1 py-2.5" disabled={saving}>
                {saving ? '⏳ Saving...' : '💾 Save Changes'}
              </button>
              <button type="button" className="btn-secondary px-6"
                onClick={() => setEditMode(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}